import { inputGradient, syncPaintInput } from "./native-paint-input";
import { changePaint } from "../native/paint-command";
import type { NativeSession } from "../native/session";
import type { NativePointer } from "../native/pointer";
import {
  changeAppearance,
  appearanceValue,
  fillAlpha,
  supportsAppearance,
  type AppearanceField,
} from "../native/appearance";

const fields: [string, AppearanceField][] = [
  ["color", "fill"],
  ["stroke-color", "stroke"],
  ["stroke-width", "strokeWidth"],
  ["opacity", "opacity"],
  ["line-style", "lineStyle"],
];
export class NativeAppearanceInspector {
  constructor(
    private session: NativeSession,
    private pointer: NativePointer,
    changed: () => void,
    message: (text: string, error?: boolean) => void,
    private prefix = "native-",
  ) {
    let preview = false;
    for (const [id, property] of fields) {
      const input = this.field(id);
      const apply = (commit: boolean) => {
        if (!commit && !preview) {
          session.begin();
          preview = true;
        }
        if (
          !pointer.active &&
          (input.type === "checkbox" || input.value.trim())
        ) {
          try {
            const value =
              input.type === "checkbox"
                ? input.checked
                : input.type === "number"
                  ? Number(input.value) / (property === "opacity" ? 100 : 1)
                  : input.value;
            const channel = input.dataset.colorChannel;
            const nodes =
              (property === "fill" || property === "stroke") &&
              (channel === "alpha" ||
                channel === "color" ||
                channel === "gradient")
                ? changePaint(
                    session.selectedNodes,
                    property,
                    input.value,
                    Number(input.dataset.colorAlpha ?? 1),
                    channel,
                    inputGradient(input),
                  )
                : changeAppearance(session.selectedNodes, property, value);
            session.replaceMany(nodes);
            // Read back the applied opacity synchronously: activating a dormant
            // fill changes its effective alpha without editing its stored alpha.
            if (property === "fill" && nodes[0])
              input.dataset.displayAlpha = String(fillAlpha(nodes[0]));
            if (commit) {
              session.finish();
              preview = false;
            }
          } catch (error) {
            message((error as Error).message, true);
          }
        }
        if (commit) input.blur();
        changed();
      };
      input.addEventListener("change", () => apply(true));
      if (input.getAttribute("type") === "color")
        input.addEventListener("input", () => apply(false));
      input.addEventListener("keydown", (event) => {
        if (event.key === "Escape") {
          if (preview) {
            session.cancel();
            preview = false;
            changed();
          }
          this.render(true);
          input.blur();
          event.stopPropagation();
        }
      });
    }
  }
  private field(id: string) {
    return document.getElementById(this.prefix + id) as HTMLInputElement;
  }
  render(restore = false) {
    for (const [id, property] of fields) {
      const input = this.field(id),
        nodes = this.session.selectedNodes.filter((n) =>
          supportsAppearance(n, property),
        );
      input.disabled = this.pointer.active || !nodes.length;
      input.closest("label")!.hidden = !nodes.length;
      const values = nodes.map((n) => appearanceValue(n, property)),
        mixed = values.some((v) => v !== values[0]);
      const value = values[0];
      if (input.type === "checkbox") {
        input.checked = value === true;
        input.indeterminate = mixed;
      } else if (restore || document.activeElement !== input) {
        if (input.type === "number")
          input.value =
            mixed || value === undefined
              ? ""
              : String(
                  Math.round(
                    Number(value) * (property === "opacity" ? 100 : 1) * 10000,
                  ) / 10000,
                );
        else if (typeof value === "string")
          input.value = property === "lineStyle" && mixed ? "" : value;
      }
      input.placeholder = mixed ? "Разные" : "";
      if (input.getAttribute("type") === "color") {
        const first = nodes[0];
        syncPaintInput(input, first?.appearance[property + "Gradient"]);
        input.dataset.displayAlpha = String(
          first
            ? property === "fill"
              ? fillAlpha(first)
              : (first.appearance.strokeAlpha ?? 1)
            : 1,
        );
        input.title = mixed
          ? "Разные цвета — выбор применит один цвет ко всем выбранным фигурам"
          : "";
      }
    }
  }
}
